import { normalizeLocale, type SupportedLocale } from './config';
import {
  ARCHIVE_DISK_ERROR,
  ARCHIVE_LIMIT_ERROR,
} from '../../trigger/lib/download-archive-budget';

type Copy = {
  tooLarge: string;
  disk: string;
  estimated: string;
  atLeast: string;
};
const COPY: Record<SupportedLocale, Copy> = {
  en: {
    tooLarge: 'Archive exceeds the size limit. Download fewer files.',
    disk: 'Not enough temporary storage. Try again later.',
    estimated: 'Estimated',
    atLeast: 'At least',
  },
  ar: {
    tooLarge: 'يتجاوز الأرشيف حد الحجم. نزّل ملفات أقل.',
    disk: 'مساحة التخزين المؤقت غير كافية. حاول لاحقًا.',
    estimated: 'تقديري',
    atLeast: 'على الأقل',
  },
  es: {
    tooLarge: 'El archivo supera el límite de tamaño. Descarga menos archivos.',
    disk: 'No hay suficiente espacio temporal. Inténtalo más tarde.',
    estimated: 'Estimado',
    atLeast: 'Al menos',
  },
  'pt-BR': {
    tooLarge: 'O arquivo excede o limite de tamanho. Baixe menos arquivos.',
    disk: 'Espaço temporário insuficiente. Tente mais tarde.',
    estimated: 'Estimado',
    atLeast: 'Pelo menos',
  },
  fr: {
    tooLarge:
      'L’archive dépasse la taille limite. Téléchargez moins de fichiers.',
    disk: 'Stockage temporaire insuffisant. Réessayez plus tard.',
    estimated: 'Estimation',
    atLeast: 'Au moins',
  },
  it: {
    tooLarge: 'L’archivio supera il limite di dimensione. Scarica meno file.',
    disk: 'Spazio temporaneo insufficiente. Riprova più tardi.',
    estimated: 'Stima',
    atLeast: 'Almeno',
  },
  ko: {
    tooLarge:
      '압축 파일이 크기 제한을 초과합니다. 파일 수를 줄여 다운로드하세요.',
    disk: '임시 저장 공간이 부족합니다. 나중에 다시 시도하세요.',
    estimated: '예상',
    atLeast: '최소',
  },
  de: {
    tooLarge:
      'Das Archiv überschreitet die Größenbegrenzung. Laden Sie weniger Dateien herunter.',
    disk: 'Nicht genügend temporärer Speicher. Versuchen Sie es später erneut.',
    estimated: 'Geschätzt',
    atLeast: 'Mindestens',
  },
  ja: {
    tooLarge:
      'アーカイブがサイズ上限を超えています。ファイル数を減らしてください。',
    disk: '一時ストレージが不足しています。後でもう一度お試しください。',
    estimated: '推定',
    atLeast: '少なくとも',
  },
  hi: {
    tooLarge: 'आर्काइव आकार सीमा से बड़ा है। कम फ़ाइलें डाउनलोड करें।',
    disk: 'अस्थायी स्टोरेज पर्याप्त नहीं है। बाद में फिर प्रयास करें।',
    estimated: 'अनुमानित',
    atLeast: 'कम से कम',
  },
  ru: {
    tooLarge: 'Архив превышает допустимый размер. Скачайте меньше файлов.',
    disk: 'Недостаточно временного места. Повторите позже.',
    estimated: 'Примерно',
    atLeast: 'Не менее',
  },
  zh: {
    tooLarge: '压缩包超出大小限制。请减少下载的文件数量。',
    disk: '临时存储空间不足。请稍后重试。',
    estimated: '预计',
    atLeast: '至少',
  },
  'zh-TW': {
    tooLarge: '壓縮檔超出大小限制。請減少下載的檔案數量。',
    disk: '暫存空間不足。請稍後重試。',
    estimated: '預估',
    atLeast: '至少',
  },
  vi: {
    tooLarge: 'Tệp nén vượt giới hạn kích thước. Hãy tải ít tệp hơn.',
    disk: 'Không đủ dung lượng tạm thời. Hãy thử lại sau.',
    estimated: 'Ước tính',
    atLeast: 'Ít nhất',
  },
  id: {
    tooLarge: 'Arsip melebihi batas ukuran. Unduh lebih sedikit berkas.',
    disk: 'Penyimpanan sementara tidak cukup. Coba lagi nanti.',
    estimated: 'Perkiraan',
    atLeast: 'Setidaknya',
  },
};
export function getDownloadResourceError(
  message: string | undefined,
  locale: string
): string | null {
  const copy = COPY[normalizeLocale(locale)];
  if (
    message === ARCHIVE_LIMIT_ERROR ||
    message === 'FOLDER_ZIP_ARCHIVE_TOO_LARGE'
  )
    return copy.tooLarge;
  if (message === ARCHIVE_DISK_ERROR) return copy.disk;
  return null;
}
export function getDownloadSizeLabel(
  size: string,
  unknownCount: number | undefined,
  locale: string
): string {
  const copy = COPY[normalizeLocale(locale)];
  return `${unknownCount ? copy.atLeast : copy.estimated}: ${size}`;
}
